import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { toast } from 'sonner';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import { 
  Monitor,
  Server,
  Smartphone,
  Tablet,
  Download,
  Search,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  HelpCircle,
  ShieldAlert,
  ChevronLeft
} from 'lucide-react';

const OSReport = () => {
  const { can } = useAuth();
  const canRead = can('reports.inventory', 'read');

  // Estados principales
  const [category, setCategory] = useState('micro'); // 'infra' o 'micro'
  const [microType, setMicroType] = useState('workstations'); // 'workstations', 'mobile', 'tablets'
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Filtros
  const [filters, setFilters] = useState({
    search: '',
    site_id: '',
    empresa_id: '',
    cliente_id: '',
    status_id: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    sites: [],
    empresas: [],
    clientes: [],
    estados: []
  });

  // Datos
  const [stats, setStats] = useState({ total: 0, unique_builds: 0, obsolete: 0, no_os: 0 });
  const [builds, setBuilds] = useState([]);
  
  // Detalle expandido
  const [expandedBuild, setExpandedBuild] = useState(null);
  const [detailData, setDetailData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSearch, setDetailSearch] = useState('');

  // Cargar opciones de filtro
  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await api.get(`/reports/os/filter-options?category=${category}`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  }, [category]);

  // Cargar builds
  const fetchBuilds = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('category', category);
      params.append('type', category === 'infra' ? 'servers' : microType);
      
      if (filters.search) params.append('search', filters.search);
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.empresa_id) params.append('empresa_id', filters.empresa_id);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);
      if (filters.status_id) params.append('status_id', filters.status_id);

      const response = await api.get(`/reports/os/builds?${params}`);
      setStats(response.data.stats);
      setBuilds(response.data.builds);
      setExpandedBuild(null);
    } catch (error) {
      console.error('Error fetching builds:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [category, microType, filters]);

  // Cargar detalle de un build
  const fetchBuildDetail = async (osId, noOs = false, page = 1) => {
    setDetailLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('category', category);
      params.append('type', category === 'infra' ? 'servers' : microType);
      params.append('page', page.toString());
      params.append('page_size', '10');
      
      if (noOs) {
        params.append('no_os', 'true');
      } else if (osId) {
        params.append('os_id', osId.toString());
      }
      
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.empresa_id) params.append('empresa_id', filters.empresa_id);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);
      if (filters.status_id) params.append('status_id', filters.status_id);
      if (detailSearch) params.append('search', detailSearch);

      const response = await api.get(`/reports/os/detail?${params}`);
      setDetailData(response.data);
    } catch (error) {
      console.error('Error fetching detail:', error);
      toast.error('Error al cargar detalle');
    } finally {
      setDetailLoading(false);
    }
  };

  // Exportar a Excel
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('category', category);
      params.append('type', category === 'infra' ? 'servers' : microType);
      
      if (filters.search) params.append('search', filters.search);
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.empresa_id) params.append('empresa_id', filters.empresa_id);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);
      if (filters.status_id) params.append('status_id', filters.status_id);

      const response = await api.get(`/reports/os/export?${params}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_so_${category}_${microType}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Reporte exportado exitosamente');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  // Toggle expandir build
  const toggleBuild = (build) => {
    const key = build.os_id || 'no_os';
    if (expandedBuild === key) {
      setExpandedBuild(null);
    } else {
      setExpandedBuild(key);
      setDetailSearch('');
      fetchBuildDetail(build.os_id, build.os_name === 'Sin SO asignado');
    }
  };

  // Effects
  useEffect(() => {
    if (canRead) {
      fetchFilterOptions();
    }
  }, [category, canRead, fetchFilterOptions]);

  useEffect(() => {
    if (canRead) {
      fetchBuilds();
    }
  }, [category, microType, filters, canRead, fetchBuilds]);

  // Status icon helper
  const getStatusIcon = (status) => {
    switch (status) {
      case 'obsolete':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'outdated':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'current':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      default:
        return <HelpCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'obsolete':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'outdated':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'current':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Monitor className="h-8 w-8 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="text-2xl font-semibold">Reporte por Sistema Operativo</h1>
              <p className="text-sm text-muted-foreground">Analiza la distribución de sistemas operativos por build</p>
            </div>
          </div>
        </div>

        {/* Sin permiso */}
        {!canRead && (
          <Card className="border-destructive/50 bg-destructive/5">
            <CardContent className="flex items-center gap-4 py-8">
              <ShieldAlert className="h-12 w-12 text-destructive" strokeWidth={1.5} />
              <div>
                <h3 className="text-lg font-medium text-destructive">Acceso Denegado</h3>
                <p className="text-sm text-muted-foreground">No tiene permisos para acceder a este reporte.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {canRead && (
          <>
            {/* Selector de Categoría */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Categoría</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={category}
                  onValueChange={(v) => {
                    setCategory(v);
                    setFilters({ search: '', site_id: '', empresa_id: '', cliente_id: '', status_id: '' });
                  }}
                  className="flex flex-wrap gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="infra" id="infra" />
                    <Label htmlFor="infra" className="flex items-center gap-2 cursor-pointer">
                      <Server className="h-4 w-4" />
                      Infraestructura (Servidores)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="micro" id="micro" />
                    <Label htmlFor="micro" className="flex items-center gap-2 cursor-pointer">
                      <Monitor className="h-4 w-4" />
                      Microinformática
                    </Label>
                  </div>
                </RadioGroup>

                {/* Sub-tipo para Micro */}
                {category === 'micro' && (
                  <div className="mt-4 flex gap-2">
                    <Button
                      variant={microType === 'workstations' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setMicroType('workstations')}
                      className="gap-2"
                    >
                      <Monitor className="h-4 w-4" />
                      Workstations
                    </Button>
                    <Button
                      variant={microType === 'mobile' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setMicroType('mobile')}
                      className="gap-2"
                    >
                      <Smartphone className="h-4 w-4" />
                      Celulares
                    </Button>
                    <Button
                      variant={microType === 'tablets' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setMicroType('tablets')}
                      className="gap-2"
                    >
                      <Tablet className="h-4 w-4" />
                      Tablets
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Filtros */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-xs">Buscar SO/Build</Label>
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Ej: Windows 10 19045..."
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="pl-8"
                        data-testid="search-os"
                      />
                    </div>
                  </div>
                  
                  <div className="w-[150px]">
                    <Label className="text-xs">Site</Label>
                    <Select
                      value={filters.site_id || "todos"}
                      onValueChange={(v) => setFilters(prev => ({ ...prev, site_id: v === "todos" ? "" : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        {filterOptions.sites?.map(s => (
                          <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {category === 'micro' && (
                    <>
                      <div className="w-[150px]">
                        <Label className="text-xs">Empresa</Label>
                        <Select
                          value={filters.empresa_id || "todos"}
                          onValueChange={(v) => setFilters(prev => ({ ...prev, empresa_id: v === "todos" ? "" : v }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Todas" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todos">Todas</SelectItem>
                            {filterOptions.empresas?.map(e => (
                              <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="w-[150px]">
                        <Label className="text-xs">Cliente</Label>
                        <Select
                          value={filters.cliente_id || "todos"}
                          onValueChange={(v) => setFilters(prev => ({ ...prev, cliente_id: v === "todos" ? "" : v }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Todos" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todos">Todos</SelectItem>
                            {filterOptions.clientes?.map(c => (
                              <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  <div className="w-[150px]">
                    <Label className="text-xs">Estado</Label>
                    <Select
                      value={filters.status_id || "todos"}
                      onValueChange={(v) => setFilters(prev => ({ ...prev, status_id: v === "todos" ? "" : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        {filterOptions.estados?.map(e => (
                          <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={handleExport}
                    disabled={exporting || builds.length === 0}
                    variant="outline"
                    className="gap-2"
                  >
                    {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    Exportar
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{stats.total}</div>
                  <p className="text-xs text-muted-foreground">Total Equipos</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{stats.unique_builds}</div>
                  <p className="text-xs text-muted-foreground">Builds Únicos</p>
                </CardContent>
              </Card>
              <Card className="border-red-200 dark:border-red-900/50">
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-red-600">{stats.obsolete}</div>
                  <p className="text-xs text-muted-foreground">SO Obsoletos</p>
                </CardContent>
              </Card>
              <Card className="border-gray-200 dark:border-gray-700">
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-gray-500">{stats.no_os}</div>
                  <p className="text-xs text-muted-foreground">Sin SO Asignado</p>
                </CardContent>
              </Card>
            </div>

            {/* Tabla de Builds */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Distribución por Sistema Operativo</CardTitle>
                <CardDescription>
                  Click en una fila para ver el detalle de equipos
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : builds.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    No se encontraron datos
                  </div>
                ) : (
                  <div className="space-y-2">
                    {builds.map((build) => {
                      const key = build.os_id || 'no_os';
                      const isExpanded = expandedBuild === key;
                      
                      return (
                        <Collapsible key={key} open={isExpanded}>
                          <CollapsibleTrigger asChild>
                            <div
                              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                                isExpanded 
                                  ? 'bg-primary/10 border border-primary/20' 
                                  : 'bg-muted/50 hover:bg-muted'
                              }`}
                              onClick={() => toggleBuild(build)}
                            >
                              <div className="flex items-center gap-3">
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                )}
                                {getStatusIcon(build.status)}
                                <span className="font-medium">{build.os_name}</span>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(build.status)}`}>
                                  {build.count} ({build.percentage}%)
                                </span>
                              </div>
                            </div>
                          </CollapsibleTrigger>
                          
                          <CollapsibleContent>
                            <div className="mt-2 ml-6 p-4 bg-background border rounded-lg">
                              {/* Buscador dentro del detalle */}
                              <div className="flex gap-2 mb-4">
                                <Input
                                  placeholder="Buscar en este grupo..."
                                  value={detailSearch}
                                  onChange={(e) => setDetailSearch(e.target.value)}
                                  className="max-w-xs"
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => fetchBuildDetail(build.os_id, build.os_name === 'Sin SO asignado')}
                                >
                                  <Search className="h-4 w-4" />
                                </Button>
                              </div>
                              
                              {detailLoading ? (
                                <div className="flex items-center justify-center py-8">
                                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                </div>
                              ) : (
                                <>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="border-b">
                                          <th className="text-left py-2 px-3 font-medium">Nombre</th>
                                          {category === 'micro' && (
                                            <>
                                              <th className="text-left py-2 px-3 font-medium">Usuario</th>
                                              <th className="text-left py-2 px-3 font-medium">Legajo</th>
                                            </>
                                          )}
                                          {category === 'infra' && (
                                            <th className="text-left py-2 px-3 font-medium">IP</th>
                                          )}
                                          <th className="text-left py-2 px-3 font-medium">Build</th>
                                          <th className="text-left py-2 px-3 font-medium">Site</th>
                                          <th className="text-left py-2 px-3 font-medium">Estado</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {detailData.items.map((item, idx) => (
                                          <tr key={idx} className="border-b last:border-0 hover:bg-muted/50">
                                            <td className="py-2 px-3">{item.nombre}</td>
                                            {category === 'micro' && (
                                              <>
                                                <td className="py-2 px-3 text-muted-foreground">{item.usuario || '-'}</td>
                                                <td className="py-2 px-3 text-muted-foreground">{item.legajo || '-'}</td>
                                              </>
                                            )}
                                            {category === 'infra' && (
                                              <td className="py-2 px-3 font-mono text-xs">{item.ip || '-'}</td>
                                            )}
                                            <td className="py-2 px-3 font-mono text-xs text-primary">{item.build_full || item.build || '-'}</td>
                                            <td className="py-2 px-3 text-muted-foreground">{item.site_name || '-'}</td>
                                            <td className="py-2 px-3">
                                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                item.status_name?.toLowerCase().includes('activo')
                                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                                              }`}>
                                                {item.status_name || '-'}
                                              </span>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                  
                                  {/* Paginación del detalle */}
                                  {detailData.pages > 1 && (
                                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                                      <span className="text-sm text-muted-foreground">
                                        Mostrando {((detailData.page - 1) * 10) + 1}-{Math.min(detailData.page * 10, detailData.total)} de {detailData.total}
                                      </span>
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => fetchBuildDetail(build.os_id, build.os_name === 'Sin SO asignado', detailData.page - 1)}
                                          disabled={detailData.page <= 1}
                                        >
                                          <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                        <span className="flex items-center px-2 text-sm">
                                          {detailData.page} / {detailData.pages}
                                        </span>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => fetchBuildDetail(build.os_id, build.os_name === 'Sin SO asignado', detailData.page + 1)}
                                          disabled={detailData.page >= detailData.pages}
                                        >
                                          <ChevronRight className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                  </div>
                )}

                {/* Leyenda */}
                <div className="flex items-center gap-6 mt-6 pt-4 border-t text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-500" />
                    <span>Obsoleto (EOL)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                    <span>Desactualizado</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Vigente</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                    <span>Sin SO</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default OSReport;
